import { MEETING_SHARE_ACCESS_TOKEN_HEADER } from "../constants/meetings";
import type {
  IBulkRemoveMeetingSharesParams,
  IBulkRemoveMeetingSharesResponse,
  IBulkShareMeetingParams,
  IBulkShareMeetingResponse,
  ICreateMeetingFeedbackParams,
  ICreateMeetingPublicShareParams,
  ICreateMeetingPublicShareResponse,
  ICreateOrUpdateMeetingReactionParams,
  IGetMeetingsParams,
  IGetPublicSharedMeetingParams,
  IJoinMeetingInstantPayload,
  IJoinMeetingInstantResponse,
  IMeeting,
  IMeetingPublicShare,
  IMeetingPublicSharesListResponse,
  IMeetingPublicShareStatusResponse,
  IMeetingPublicShareVerifyResponse,
  IMeetingShare,
  IMeetingSharesListResponse,
  IPaginatedMeetingsResponse,
  IRenameMeetingSpeakerParams,
  IRenameMeetingSpeakerResponse,
  IShareMeetingParams,
  IToggleFavouriteMeetingResponse,
  IUpdateMeetingParams,
  IVerifyPublicSharedMeetingParams,
} from "../types/meetings";
import type { IVideoStreamUrlResponse } from "../types/vstr-video";
import { encodePathSegment } from "../utils/encode-path-segment";
import type { ApiClient } from "./client";

export class MeetingsApi {
  constructor(private readonly apiClient: ApiClient) {}

  async getMeetings(
    params: IGetMeetingsParams = {},
  ): Promise<IPaginatedMeetingsResponse> {
    const response = await this.apiClient.get<IPaginatedMeetingsResponse>(
      "/meetings",
      {
        params,
      },
    );
    return response.data;
  }

  async getMeetingByUuid(meetingUuid: string): Promise<IMeeting> {
    const response = await this.apiClient.get<IMeeting>(
      `/meetings/${encodePathSegment(meetingUuid)}`,
    );
    return response.data;
  }

  async updateMeeting({
    meetingUuid,
    payload,
  }: IUpdateMeetingParams): Promise<IMeeting> {
    const response = await this.apiClient.put<IMeeting>(
      `/meetings/${encodePathSegment(meetingUuid)}`,
      payload,
    );
    return response.data;
  }

  // NOTE: NSAI-7656 — renames a speaker in the stored transcript, in every
  // segment carrying the label or in the one picked, on the server.
  async renameMeetingSpeaker({
    meetingUuid,
    payload,
  }: IRenameMeetingSpeakerParams): Promise<IRenameMeetingSpeakerResponse> {
    const response = await this.apiClient.patch<IRenameMeetingSpeakerResponse>(
      `/meetings/${encodePathSegment(meetingUuid)}/transcript/speakers`,
      payload,
    );
    return response.data;
  }

  async deleteMeeting(meetingUuid: string): Promise<void> {
    await this.apiClient.delete(`/meetings/${encodePathSegment(meetingUuid)}`);
  }

  async toggleArchiveMeeting(meetingUuid: string): Promise<void> {
    await this.apiClient.post(
      `/meetings/${encodePathSegment(meetingUuid)}/archive`,
      {},
    );
  }

  async toggleFavouriteMeeting(
    meetingUuid: string,
  ): Promise<IToggleFavouriteMeetingResponse> {
    const response = await this.apiClient.post<IToggleFavouriteMeetingResponse>(
      `/meetings/${encodePathSegment(meetingUuid)}/favourite`,
      {},
    );
    return response.data;
  }

  // NOTE: Re-dispatches the processor workflow for a meeting that
  // landed in a retryable failure state. Server returns 409 if the
  // status isn't retryable or retry_count is already at the cap —
  // callers should let the toast layer surface that. Response is the
  // updated meeting with retryCount incremented and status=QUEUED.
  async retryMeeting(meetingUuid: string): Promise<IMeeting> {
    const response = await this.apiClient.post<IMeeting>(
      `/meetings/${encodePathSegment(meetingUuid)}/retry`,
      {},
    );
    return response.data;
  }

  async createOrUpdateMeetingReaction({
    meetingUuid,
    payload,
  }: ICreateOrUpdateMeetingReactionParams): Promise<void> {
    await this.apiClient.post(
      `/meetings/${encodePathSegment(meetingUuid)}/reactions`,
      payload,
    );
  }

  async createMeetingFeedback({
    meetingUuid,
    payload,
  }: ICreateMeetingFeedbackParams): Promise<void> {
    await this.apiClient.post(
      `/meetings/${encodePathSegment(meetingUuid)}/feedback`,
      payload,
    );
  }

  async shareMeeting({
    meetingUuid,
    payload,
  }: IShareMeetingParams): Promise<IMeetingShare> {
    const response = await this.apiClient.post<IMeetingShare>(
      `/meetings/${encodePathSegment(meetingUuid)}/share`,
      payload,
    );
    return response.data;
  }

  async bulkShareMeeting({
    meetingUuid,
    payload,
  }: IBulkShareMeetingParams): Promise<IBulkShareMeetingResponse> {
    const response = await this.apiClient.post<IBulkShareMeetingResponse>(
      `/meetings/${encodePathSegment(meetingUuid)}/shares/bulk`,
      payload,
    );
    return response.data;
  }

  async getMeetingShares(
    meetingUuid: string,
  ): Promise<IMeetingSharesListResponse> {
    const response = await this.apiClient.get<IMeetingSharesListResponse>(
      `/meetings/${encodePathSegment(meetingUuid)}/shares`,
    );
    return response.data;
  }

  async removeMeetingShare(shareUuid: string): Promise<void> {
    await this.apiClient.delete(
      `/meetings/shares/${encodePathSegment(shareUuid)}`,
    );
  }

  async bulkRemoveMeetingShares({
    meetingUuid,
    payload,
  }: IBulkRemoveMeetingSharesParams): Promise<IBulkRemoveMeetingSharesResponse> {
    const response =
      await this.apiClient.delete<IBulkRemoveMeetingSharesResponse>(
        `/meetings/${encodePathSegment(meetingUuid)}/shares/bulk-remove`,
        { data: payload },
      );
    return response.data;
  }

  async joinMeetingInstant(
    payload: IJoinMeetingInstantPayload,
  ): Promise<IJoinMeetingInstantResponse> {
    const response = await this.apiClient.post<IJoinMeetingInstantResponse>(
      "/meetings/join-instant",
      payload,
    );
    return response.data;
  }

  // NOTE: Public (no-account) meeting share — owner management (NSAI-3853).

  async getMeetingPublicShare(
    meetingUuid: string,
  ): Promise<IMeetingPublicShare | null> {
    const response = await this.apiClient.get<IMeetingPublicShare | null>(
      `/meetings/${encodePathSegment(meetingUuid)}/public-share`,
    );
    return response.data;
  }

  // NOTE: Every live public link the caller owns (NSAI-5027) — powers the
  // "Shared Meetings" tab. Server-side route is registered before /meetings/:id,
  // so "public-shares" is not read as a meeting uuid.
  async getMeetingPublicShares(): Promise<IMeetingPublicSharesListResponse> {
    const response = await this.apiClient.get<IMeetingPublicSharesListResponse>(
      "/meetings/public-shares",
    );
    return response.data;
  }

  async createMeetingPublicShare({
    meetingUuid,
    payload,
  }: ICreateMeetingPublicShareParams): Promise<ICreateMeetingPublicShareResponse> {
    const response =
      await this.apiClient.post<ICreateMeetingPublicShareResponse>(
        `/meetings/${encodePathSegment(meetingUuid)}/public-share`,
        payload,
      );
    return response.data;
  }

  async revokeMeetingPublicShare(meetingUuid: string): Promise<void> {
    await this.apiClient.post(
      `/meetings/${encodePathSegment(meetingUuid)}/public-share/revoke`,
      {},
    );
  }

  // NOTE: Public (no-account) meeting share — anonymous viewer side. All of
  // these opt out of the client's session auth handling (skipAuthHandling): the
  // viewer has no NSAI session, and a 401 means a bad password, not sign-out.

  async getPublicSharedMeetingStatus(
    shareToken: string,
  ): Promise<IMeetingPublicShareStatusResponse> {
    const response =
      await this.apiClient.get<IMeetingPublicShareStatusResponse>(
        `/meetings/shared/${encodePathSegment(shareToken)}`,
        { skipAuthHandling: true },
      );
    return response.data;
  }

  async verifyPublicSharedMeetingPassword({
    shareToken,
    payload,
  }: IVerifyPublicSharedMeetingParams): Promise<IMeetingPublicShareVerifyResponse> {
    const response =
      await this.apiClient.post<IMeetingPublicShareVerifyResponse>(
        `/meetings/shared/${encodePathSegment(shareToken)}/verify`,
        payload,
        { skipAuthHandling: true },
      );
    return response.data;
  }

  async getPublicSharedMeeting({
    shareToken,
    accessToken,
  }: IGetPublicSharedMeetingParams): Promise<IMeeting> {
    const response = await this.apiClient.get<IMeeting>(
      `/meetings/shared/${encodePathSegment(shareToken)}/meeting`,
      {
        skipAuthHandling: true,
        headers: { [MEETING_SHARE_ACCESS_TOKEN_HEADER]: accessToken },
      },
    );
    return response.data;
  }

  async getPublicSharedMeetingStreamUrl({
    shareToken,
    accessToken,
  }: IGetPublicSharedMeetingParams): Promise<IVideoStreamUrlResponse> {
    const response = await this.apiClient.post<IVideoStreamUrlResponse>(
      `/meetings/shared/${encodePathSegment(shareToken)}/stream-url`,
      {},
      {
        skipAuthHandling: true,
        headers: { [MEETING_SHARE_ACCESS_TOKEN_HEADER]: accessToken },
      },
    );
    return response.data;
  }
}
